import { DiscoApp } from 'discoui';
import 'discoui/dist/discoui.css';
import './styles.css';
import { AnimState, CortanaAnimation } from './cortana-anim.js';

const TOKEN_KEY = 'recortana_one_live_access';
const VERIFIER_KEY = 'recortana_pkce_verifier';
const ACCENT_KEY = 'recortana_accent';
const ACCENTS = ['#0078D7', '#1BA1E2', '#00ABA9', '#008A00', '#A4C400', '#F0A30A', '#FA6800', '#E51400', '#D80073', '#AA00FF', '#6A00FF', '#647687'];
const GREET_MORNING = ['Good morning!', 'Hello there.', 'Hi there!', 'Hey. What can I do for you?', 'How can I help?', "What's on your mind?"];
const GREET_AFTERNOON = ['Hi there!', 'Hello!', 'Hey there.', 'How can I help?', 'Need anything?', 'What can I help you with?', "What's up?"];
const GREET_EVENING = ['Good evening.', 'Hello there.', 'Hi!', 'Hey. What can I do for you?', 'How can I help?', 'Need anything?'];
const GREET_NIGHT = ['Hello.', 'Hi there!', 'How can I help?', "What's on your mind?", 'Still up? What can I do for you?'];
const GREET_ALL = [
  'Hello there.', 'Hello!', 'Hello. What can I do for you?', 'Hey there.', 'Hey. What can I do for you?',
  'Hi there!', 'Hi!', 'Hi! How can I help?', "Hi! What's up?", "Hi. What's on your mind?", 'Greetings!', 'Oh hey!',
  'Anything I can do for you?', 'Anything I can get started?', 'Can I be of assistance?', 'How can I help?',
  'Need anything?', 'Need something?', 'Something I can do for you?', 'What can I help you with?',
  'What would you like me to do?', "What's on your mind?", "What's up?", 'Ask me anything.',
  "I'm Cortana! I can help with your day.",
];
let suggestTimer = 0;
let suggestGeneration = 0;
let pickingSuggest = false;
let lastQuery = '';
let bingQuery = '';
let articles = [];

const $ = (id) => document.getElementById(id);
let token = localStorage.getItem(TOKEN_KEY) || '';
let me = null;
let anim = null;
let voice = null;
let frame = null;
let settingsPage = null;
let detailPage = null;

function val(el) { return el ? String(el.value || '').trim() : ''; }

async function api(path, options = {}) {
  const headers = Object.assign({ Accept: 'application/json' }, options.headers || {});
  if (token) headers.Authorization = 'Bearer ' + token;
  if (options.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
  const res = await fetch(path, Object.assign({}, options, { headers }));
  const text = await res.text();
  let data = {};
  try { data = text ? JSON.parse(text) : {}; } catch { data = { raw: text }; }
  if (!res.ok) {
    const err = new Error(data.error || data.message || ('HTTP ' + res.status));
    err.status = res.status;
    throw err;
  }
  return data;
}

function randomString(len) {
  const bytes = new Uint8Array(len);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => ('0' + b.toString(16)).slice(-2)).join('');
}

async function sha256(text) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  let str = '';
  new Uint8Array(digest).forEach((b) => { str += String.fromCharCode(b); });
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function pick(list) { return list[Math.floor(Math.random() * list.length)]; }

function withName(greetingText) {
  const name = ((me && me.name) || '').trim();
  if (!name || !greetingText || greetingText.length > 28 || Math.random() >= 0.45) return greetingText;
  if (greetingText.endsWith('!') || greetingText.endsWith('.')) {
    return greetingText.slice(0, -1).trimEnd() + ', ' + name + greetingText.slice(-1);
  }
  return greetingText + ', ' + name + '.';
}

function greeting() {
  const mode = (me && me.greeting_mode) || 'random';
  if ((mode === 'custom' || mode === 'specific') && me && me.greeting) return me.greeting;
  const hour = new Date().getHours();
  let pool = GREET_NIGHT;
  if (hour >= 5 && hour < 12) pool = GREET_MORNING;
  else if (hour >= 12 && hour < 17) pool = GREET_AFTERNOON;
  else if (hour >= 17 && hour < 22) pool = GREET_EVENING;
  if (Math.random() < 0.25) pool = GREET_ALL;
  return withName(pick(pool));
}

function showIdle(text) {
  $('animation-container').className = 'idle';
  $('results-display').textContent = text || greeting();
  $('ai-note').hidden = true;
  $('weather-card').hidden = true;
  $('related-cards').innerHTML = '';
  paintIdleExtras();
}

function showActive(text, extra) {
  $('animation-container').className = 'active' + ($('bing-link').hidden ? '' : ' bing');
  $('results-display').textContent = text || '';
  $('ai-note').hidden = !(extra && extra.ai);
  const card = $('weather-card');
  const widget = ((extra && extra.widgets) || []).find((item) => item && item.type === 'weather');
  const data = widget && widget.data;
  if (!data) {
    card.hidden = true;
    return;
  }
  const c = data.temperature && data.temperature.celsius;
  card.hidden = false;
  card.innerHTML = '';
  const place = document.createElement('span');
  place.textContent = data.location || '';
  const temp = document.createElement('b');
  temp.textContent = c != null ? Math.round(c) + '°' : '';
  const condition = document.createElement('span');
  condition.textContent = data.condition || '';
  card.append(place, temp, condition);
}

function speak(text) {
  if (voice) voice.pause();
  if (!text) return Promise.resolve();
  anim.play(AnimState.SPEAK_BEGIN);
  return new Promise((resolve) => {
    const audio = new Audio('/tts?text=' + encodeURIComponent(text.slice(0, 500)));
    voice = audio;
    const done = () => {
      if (voice === audio) voice = null;
      anim.play(AnimState.SPEAK_END);
      resolve();
    };
    audio.addEventListener('ended', done);
    audio.addEventListener('error', done);
    audio.play().catch(done);
  });
}

async function saveMe(patch) {
  me = await api('/me', { method: 'PUT', body: JSON.stringify(patch) });
  paintProfile();
}

function paintProfile() {
  const signedIn = !!(me && me.sub);
  $('signed-out').hidden = signedIn;
  $('signed-in').hidden = !signedIn;
  if (!signedIn) return;
  $('profile-name').textContent = me.name || 'Signed in';
  $('profile-email').textContent = me.email || '';
  $('profile-sub').textContent = me.sub || '';
  $('profile-location').textContent = me.location || 'Not set';
  $('profile-interests').textContent = (me.interests || []).join(', ') || 'None yet';
  $('profile-notebook').textContent = (me.notebook || []).join(' · ') || 'Empty';
  $('server-host').value = me.custom_host || '';
  $('server-port').value = me.custom_port || '';
}

async function refreshHa() {
  if (!token) return;
  try {
    const status = await api('/ha/status');
    $('profile-ha').textContent = status.connected ? 'Connected · ' + (status.base_url || '') : 'Not connected';
    if (status.base_url) $('ha-url').value = status.base_url;
  } catch {
    $('profile-ha').textContent = 'Not connected';
  }
}

async function startSignIn() {
  $('auth-error').textContent = '';
  const verifier = randomString(48);
  localStorage.setItem(VERIFIER_KEY, verifier);
  const challenge = await sha256(verifier);
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: 'oli_59a82513ccf399602815cb47f32351af',
    redirect_uri: location.origin + '/auth/callback',
    scope: 'openid profile email read_profile offline_access',
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state: randomString(16),
  });
  try {
    const cfg = await api('/auth/config');
    if (cfg.client_id) params.set('client_id', cfg.client_id);
  } catch { /* default client */ }
  location.href = 'https://id.live.net.co/connect/authorize?' + params.toString();
}

function timezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
}

function teaser(markdown) {
  const text = String(markdown || '').replace(/^#{1,6}\s*/gm, '').replace(/[*_`]/g, '').replace(/\s+/g, ' ').trim();
  return text.length <= 110 ? text : text.slice(0, 107).trim() + '...';
}

function paintIdleExtras() {
  const interests = (me && me.interests) || [];
  const signedIn = !!(me && me.sub);
  const has = interests.length > 0;
  $('interests-prompt').hidden = !signedIn || has;
  $('add-interests').hidden = !signedIn || has;
  const status = $('article-status');
  const host = $('article-cards');
  host.innerHTML = '';
  if (!has) {
    status.hidden = true;
    return;
  }
  status.hidden = !status.textContent;
  articles.forEach((article) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'story';
    const title = document.createElement('b');
    title.textContent = article.title || 'For you';
    button.appendChild(title);
    const line = teaser(article.markdown);
    if (line) {
      const blurb = document.createElement('span');
      blurb.textContent = line;
      button.appendChild(blurb);
    }
    button.addEventListener('click', () => openArticle(article));
    host.appendChild(button);
  });
  const follow = document.createElement('button');
  follow.type = 'button';
  follow.className = 'story';
  const heading = document.createElement('b');
  heading.textContent = 'Follow up on your interests';
  const sub = document.createElement('span');
  sub.textContent = 'Catch up on ' + interests.join(', ') + '.';
  follow.append(heading, sub);
  follow.addEventListener('click', () => ask('Catch me up on my interests. What\'s the latest on ' + interests.join(', ') + '?'));
  host.appendChild(follow);
}

async function refreshFeed() {
  if (!token) return;
  try {
    const feed = await api('/interests/feed?timezone=' + encodeURIComponent(timezone()));
    if (me) me.interests = feed.interests || me.interests;
    articles = feed.articles || [];
    $('article-status').textContent = feed.refreshing
      ? (articles.length ? 'Updating stories for ' + (feed.label || 'you') + '...' : 'Finding stories for you...')
      : '';
    $('article-status').hidden = !$('article-status').textContent;
    if ($('animation-container').classList.contains('idle')) paintIdleExtras();
    if (feed.refreshing) setTimeout(refreshFeed, 3000);
  } catch { /* signed out or server not restarted */ }
}

function openArticle(article) {
  hideSuggest();
  $('article-title').textContent = article.title || 'story';
  const body = $('article-body');
  body.innerHTML = '';
  String(article.markdown || '').split(/\n{2,}/).forEach((block) => {
    const p = document.createElement('p');
    p.textContent = block.replace(/^#{1,6}\s*/gm, '').trim();
    if (p.textContent) body.appendChild(p);
  });
  $('article-pane').hidden = false;
}

function paintRelated(questions) {
  const host = $('related-cards');
  host.innerHTML = '';
  (questions || []).slice(0, 3).forEach((question) => {
    const text = String(question || '').trim();
    if (!text) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'related';
    button.textContent = text;
    button.addEventListener('click', () => ask(text));
    host.appendChild(button);
  });
}

function setBingQuery(query) {
  bingQuery = query || '';
  $('bing-link').hidden = !bingQuery;
  $('animation-container').classList.toggle('bing', !!bingQuery && $('animation-container').classList.contains('active'));
}

async function openSearch(query) {
  const text = (query || bingQuery || '').trim();
  if (!text) return;
  hideSuggest();
  setBingQuery(text);
  $('search-query').textContent = text;
  const hits = $('search-hits');
  hits.innerHTML = '';
  const waiting = document.createElement('p');
  waiting.className = 'hint';
  waiting.textContent = 'Searching Bing...';
  hits.appendChild(waiting);
  $('search-pane').hidden = false;
  try {
    const result = await api('/search?q=' + encodeURIComponent(text));
    hits.innerHTML = '';
    if (!(result.hits || []).length) {
      const empty = document.createElement('p');
      empty.textContent = 'No results found. Try a different spelling or search term.';
      hits.appendChild(empty);
    }
    (result.hits || []).forEach((hit) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'search-hit';
      const title = document.createElement('b');
      title.textContent = hit.title || '';
      button.appendChild(title);
      if (hit.snippet) {
        const snippet = document.createElement('span');
        snippet.textContent = hit.snippet;
        button.appendChild(snippet);
      }
      const url = document.createElement('em');
      url.textContent = hit.url || '';
      button.appendChild(url);
      button.addEventListener('click', () => { if (hit.url) window.open(hit.url, '_blank', 'noopener'); });
      hits.appendChild(button);
    });
    const more = document.createElement('button');
    more.type = 'button';
    more.className = 'wp-item';
    more.textContent = 'See more results on Bing.com';
    more.addEventListener('click', () => window.open('https://www.bing.com/search?q=' + encodeURIComponent(text), '_blank', 'noopener'));
    hits.appendChild(more);
  } catch (err) {
    hits.innerHTML = '';
    const failed = document.createElement('p');
    failed.textContent = err.message;
    hits.appendChild(failed);
  }
}

function suggestRow(title, subtitle, icon, askRow, action) {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'suggest-row' + (askRow ? ' ask' : '');
  const img = document.createElement('img');
  img.src = icon;
  img.alt = '';
  const text = document.createElement('span');
  const heading = document.createElement('b');
  heading.textContent = title;
  text.appendChild(heading);
  if (subtitle) {
    const sub = document.createElement('span');
    sub.textContent = subtitle;
    text.appendChild(sub);
  }
  button.append(img, text);
  button.addEventListener('mousedown', () => { pickingSuggest = true; });
  button.addEventListener('click', () => {
    hideSuggest();
    $('search-bar').value = '';
    $('search-bar').placeholder = 'Ask me anything';
    action();
    pickingSuggest = false;
  });
  return button;
}

function hideSuggest() {
  suggestGeneration += 1;
  clearTimeout(suggestTimer);
  const panel = $('suggest-panel');
  if (panel.hidden) return;
  panel.classList.remove('open');
  panel.classList.add('closing');
  setTimeout(() => {
    panel.hidden = true;
    panel.classList.remove('closing');
    $('suggest-list').innerHTML = '';
  }, 180);
}

function refreshSuggest() {
  const query = $('search-bar').value.trim();
  if (!query || document.activeElement !== $('search-bar')) {
    hideSuggest();
    return;
  }
  const list = $('suggest-list');
  const panel = $('suggest-panel');
  list.innerHTML = '';
  list.appendChild(suggestRow('Ask Cortana', query, '/assets/chrome/cortana.png', true, () => ask(query)));
  list.appendChild(suggestRow('Search Bing', '"' + query + '"', '/assets/chrome/search.png', false, () => openSearch(query)));
  panel.hidden = false;
  panel.classList.remove('closing');
  panel.getBoundingClientRect();
  panel.classList.add('open');
  const generation = ++suggestGeneration;
  clearTimeout(suggestTimer);
  suggestTimer = setTimeout(async () => {
    try {
      const result = await api('/suggest?q=' + encodeURIComponent(query));
      if (generation !== suggestGeneration) return;
      (result.suggestions || []).slice(0, 2).forEach((item) => {
        if (item.toLowerCase() === query.toLowerCase()) return;
        list.appendChild(suggestRow(item, '', '/assets/chrome/search.png', false, () => {
          $('search-bar').value = item;
          $('search-bar').focus();
          refreshSuggest();
        }));
      });
    } catch { /* suggestions are optional */ }
  }, 1000);
}

async function ask(query) {
  const text = (query || '').trim();
  if (!text) return;
  hideSuggest();
  $('search-bar').value = '';
  $('search-bar').placeholder = 'Ask me anything';
  lastQuery = text;
  setBingQuery('');
  if (!token) {
    showActive('Sign in under settings with your One Live ID first.');
    return;
  }
  showActive(text);
  anim.play(AnimState.THINKING);
  try {
    const reply = await api('/chat', {
      method: 'POST',
      body: JSON.stringify({ query: text, timezone: timezone() }),
    });
    const spoken = reply.response || "Hmm. I couldn't get an answer just now.";
    showActive(spoken, { ai: reply.using_ai, widgets: reply.widgets });
    paintRelated(reply.related);
    if (reply.search_query) setBingQuery(reply.search_query);
    if ((reply.related || []).length < 3) {
      api('/related', { method: 'POST', body: JSON.stringify({ query: text, reply: spoken }) })
        .then((extra) => { if (lastQuery === text) paintRelated(extra.related); })
        .catch(() => {});
    }
    await speak(spoken);
  } catch (err) {
    showActive("Hmm. " + err.message);
    anim.play(AnimState.ERROR);
  }
}

function listen() {
  const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!Rec) {
    $('search-bar').focus();
    return;
  }
  const rec = new Rec();
  rec.lang = 'en-US';
  anim.play(AnimState.LISTEN_BEGIN);
  rec.onresult = (event) => {
    const heard = event.results && event.results[0] && event.results[0][0] ? event.results[0][0].transcript : '';
    anim.play(AnimState.LISTEN_END);
    if (heard) ask(heard);
  };
  rec.onerror = () => anim.play(AnimState.LISTEN_END);
  rec.start();
}

function pinDisco(page, css) {
  if (!page || !page.shadowRoot) return;
  let style = page.shadowRoot.querySelector('#pane-override');
  if (!style) {
    style = document.createElement('style');
    style.id = 'pane-override';
    page.shadowRoot.appendChild(style);
  }
  style.textContent = css;
}

function turnstile(el, open) {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (opened) => {
      if (settled) return;
      settled = true;
      el.removeEventListener('transitionend', onEnd);
      if (!opened) el.classList.remove('open', 'turn-out', 'turn-in');
      resolve();
    };
    const onEnd = (event) => {
      const frameEl = el.querySelector('.portrait-frame');
      if (event.target !== el && event.target !== frameEl) return;
      finish(open);
    };
    el.classList.remove('turn-in', 'turn-out');
    if (open) {
      el.classList.add('open');
      el.getBoundingClientRect();
      el.classList.add('turn-in');
    } else {
      el.getBoundingClientRect();
      el.classList.add('turn-out');
    }
    el.addEventListener('transitionend', onEnd);
    setTimeout(() => finish(open), open ? 450 : 320);
  });
}

function fitSettings() {
  const pane = $('settings-pane');
  const shell = $('settings-frame');
  const stage = $('stage');
  if (!pane || !shell || !stage || !frame) return;
  const width = stage.clientWidth;
  const height = stage.clientHeight;
  document.documentElement.style.setProperty('--disco-vw', width + 'px');
  document.documentElement.style.setProperty('--disco-vh', height + 'px');
  pane.style.setProperty('--disco-vw', width + 'px');
  pane.style.setProperty('--disco-vh', height + 'px');
  shell.style.setProperty('--disco-vw', width + 'px');
  shell.style.setProperty('--disco-vh', height + 'px');
  frame.style.position = 'absolute';
  frame.style.inset = '0';
  frame.style.width = '100%';
  frame.style.height = '100%';
  frame.style.zoom = '1';
  pinDisco(settingsPage, '.app-title{padding:18px 19px 0!important}.header-strip{padding:0 12px 8px 19px!important;overflow-x:auto!important}.header-item{font-size:36px!important;font-weight:200!important}');
  pinDisco(detailPage, '.app-title{display:none!important}.header-item{font-size:36px!important;font-weight:300!important}.header-strip{padding:36px 19px 8px!important}');
  document.querySelectorAll('disco-pivot-item').forEach((item) => {
    pinDisco(item, '.pivot-item-plain-content,.pivot-item-scroll-content{padding:0 19px 48px!important}');
  });
}

async function openDetail(name) {
  renderDetail(name);
  if (detailPage) detailPage.setAttribute('header', name);
  $('detail-back').hidden = false;
  if (frame) await frame.navigate(detailPage);
  fitSettings();
}

async function openSettings() {
  if ($('profile-pane').classList.contains('open')) {
    await turnstile($('profile-pane'), false);
  }
  if ($('settings-pane').classList.contains('open') && $('detail-back').hidden) {
    await turnstile($('settings-pane'), false);
    return;
  }
  $('detail-back').hidden = true;
  if (frame) await frame.navigate(settingsPage);
  if (!$('settings-pane').classList.contains('open')) {
    await turnstile($('settings-pane'), true);
  }
  fitSettings();
}

async function closeSettings() {
  if ($('profile-pane').classList.contains('open')) await turnstile($('profile-pane'), false);
  if ($('settings-pane').classList.contains('open')) await turnstile($('settings-pane'), false);
}

async function openProfile() {
  await turnstile($('profile-pane'), true);
}

function renderDetail(name) {
  const body = $('detail-body');
  body.innerHTML = '';
  if (name === 'theme') {
    body.innerHTML = '<p class="field-label">theme colour</p><div class="swatches" id="swatches"></div><button id="reset-theme" class="wp-item" type="button">reset theme</button>';
    const wrap = body.querySelector('#swatches');
    const current = (localStorage.getItem(ACCENT_KEY) || '#0078D7').toLowerCase();
    ACCENTS.forEach((hex) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'swatch' + (hex.toLowerCase() === current ? ' on' : '');
      btn.style.background = hex;
      btn.addEventListener('click', () => {
        applyAccent(hex);
        wrap.querySelectorAll('.swatch').forEach((el) => el.classList.toggle('on', el === btn));
      });
      wrap.appendChild(btn);
    });
    body.querySelector('#reset-theme').addEventListener('click', () => applyAccent('#0078D7'));
    return;
  }
  if (name === 'you') {
    body.innerHTML = '<p class="hint">Cortana can use this to personalize replies.</p><p class="field-label">name</p><disco-text-box id="you-name"></disco-text-box><p class="field-label">location</p><disco-text-box id="you-location"></disco-text-box><button id="you-save" class="wp-item" type="button">save</button><button id="more-options" class="wp-item" type="button">more options</button>';
    body.querySelector('#you-name').value = (me && me.name) || '';
    body.querySelector('#you-location').value = (me && me.location) || '';
    body.querySelector('#you-save').addEventListener('click', () => saveMe({
      name: val(body.querySelector('#you-name')),
      location: val(body.querySelector('#you-location')),
    }));
    body.querySelector('#more-options').addEventListener('click', () => openProfile());
    return;
  }
  if (name === 'interests') {
    body.innerHTML = '<p class="hint">Tell Cortana up to five things you care about.</p>';
    const current = (me && me.interests) || [];
    for (let i = 0; i < 5; i++) {
      const box = document.createElement('disco-text-box');
      box.className = 'edit-interest';
      box.placeholder = 'interest ' + (i + 1);
      box.value = current[i] || '';
      body.appendChild(box);
    }
    const save = document.createElement('button');
    save.type = 'button';
    save.className = 'wp-item';
    save.textContent = 'save';
    save.addEventListener('click', async () => {
      await saveMe({
        interests: [...body.querySelectorAll('.edit-interest')].map(val).filter(Boolean),
      });
      articles = [];
      refreshFeed();
    });
    body.appendChild(save);
    return;
  }
  if (name === 'notebook') {
    body.innerHTML = '<p class="hint">Cortana writes short facts here and only reads them when she asks for them.</p>';
    ((me && me.notebook) || []).forEach((fact) => {
      const row = document.createElement('p');
      row.textContent = fact;
      body.appendChild(row);
    });
    const input = document.createElement('disco-text-box');
    input.placeholder = 'Add a short fact';
    body.appendChild(input);
    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'wp-item';
    add.textContent = 'add to notebook';
    add.addEventListener('click', async () => {
      const fact = val(input);
      if (!fact) return;
      await saveMe({ notebook: ((me && me.notebook) || []).concat([fact]) });
      renderDetail('notebook');
    });
    body.appendChild(add);
    return;
  }
  if (name === 'microphone') {
    body.innerHTML = '<p class="field-label">microphone volume</p><input id="voice-boost" class="wp-range" type="range" min="1" max="4" step="0.1"><p id="voice-boost-value" class="hint"></p>';
    const slider = body.querySelector('#voice-boost');
    const label = body.querySelector('#voice-boost-value');
    slider.value = localStorage.getItem('recortana_mic_boost') || '1.5';
    const paint = () => { label.textContent = Number(slider.value).toFixed(1) + '×'; };
    paint();
    slider.addEventListener('input', () => {
      localStorage.setItem('recortana_mic_boost', slider.value);
      $('mic-boost').value = slider.value;
      $('mic-boost-value').textContent = Number(slider.value).toFixed(1) + '×';
      paint();
    });
    return;
  }
  if (name === 'greeting') {
    body.innerHTML = '<p class="field-label">idle greeting</p><select id="greet-mode" class="wp-select"><option value="random">Random</option><option value="specific">Specific</option><option value="custom">Custom</option></select><disco-text-box id="greet-text" placeholder="Hello!"></disco-text-box><button id="greet-save" class="wp-item" type="button">save</button>';
    body.querySelector('#greet-mode').value = (me && me.greeting_mode) || 'random';
    body.querySelector('#greet-text').value = (me && me.greeting) || '';
    body.querySelector('#greet-save').addEventListener('click', () => saveMe({
      greeting_mode: body.querySelector('#greet-mode').value,
      greeting: val(body.querySelector('#greet-text')),
    }));
    return;
  }
  if (name === 'time') {
    body.innerHTML = '<p class="field-label">time format</p><select id="time-format" class="wp-select"><option value="12">12-hour (AM/PM)</option><option value="24">24-hour (Military)</option></select>';
    const select = body.querySelector('#time-format');
    select.value = (me && me.time_format) || '12';
    select.addEventListener('change', () => saveMe({ time_format: select.value }));
  }
}

function applyAccent(hex) {
  document.documentElement.style.setProperty('--accent', hex);
  document.documentElement.setAttribute('disco-accent', hex);
  localStorage.setItem(ACCENT_KEY, hex);
  if (anim) anim.setAccent(hex);
}

function wire() {
  $('composer').addEventListener('submit', (event) => {
    event.preventDefault();
    ask($('search-bar').value);
  });
  $('mic-btn').addEventListener('click', listen);
  $('circle').addEventListener('click', () => anim && anim.nudge());
  $('electron-close').addEventListener('click', async () => {
    if (!$('article-pane').hidden || !$('search-pane').hidden) {
      $('article-pane').hidden = true;
      $('search-pane').hidden = true;
      return;
    }
    if ($('settings-pane').classList.contains('open') || $('profile-pane').classList.contains('open')) {
      await closeSettings();
      return;
    }
    setBingQuery('');
    showIdle();
  });
  $('add-interests').addEventListener('click', async () => {
    await openSettings();
    await openDetail('interests');
  });
  $('article-back').addEventListener('click', () => { $('article-pane').hidden = true; });
  $('search-back').addEventListener('click', () => { $('search-pane').hidden = true; });
  $('bing-link').addEventListener('click', () => openSearch(bingQuery));
  $('search-bar').addEventListener('focus', () => {
    $('search-bar').placeholder = 'Type here to search';
    if ($('search-bar').value.trim()) refreshSuggest();
  });
  $('search-bar').addEventListener('input', refreshSuggest);
  $('search-bar').addEventListener('blur', () => {
    setTimeout(() => {
      if (pickingSuggest || document.activeElement === $('search-bar')) return;
      hideSuggest();
      if (!$('search-bar').value.trim()) $('search-bar').placeholder = 'Ask me anything';
    }, 220);
  });
  $('electron-settings').addEventListener('click', () => openSettings());
  $('profile-back').addEventListener('click', () => turnstile($('profile-pane'), false));
  $('detail-back').addEventListener('click', () => openSettings());
  document.querySelectorAll('[data-detail]').forEach((btn) => {
    btn.addEventListener('click', () => openDetail(btn.getAttribute('data-detail')));
  });
  $('sign-in').addEventListener('click', () => startSignIn().catch((err) => { $('auth-error').textContent = err.message; }));
  $('sign-out').addEventListener('click', () => {
    token = '';
    me = null;
    localStorage.removeItem(TOKEN_KEY);
    paintProfile();
    showIdle('Sign in under settings to keep this conversation.');
  });
  $('clear-chat').addEventListener('click', async () => {
    if (token) await api('/conversation', { method: 'PUT', body: JSON.stringify({ messages: [] }) });
    showIdle();
  });
  $('reset-all').addEventListener('click', async () => {
    if (!token) return;
    await saveMe({ name: '', location: '', interests: [], notebook: [], memory: [], setup_complete: false });
    await api('/conversation', { method: 'PUT', body: JSON.stringify({ messages: [] }) });
    showIdle();
  });
  $('test-server').addEventListener('click', async () => {
    try {
      const health = await api('/health');
      $('server-status').textContent = health.ok ? 'Connected.' : 'The server answered, but it is not ready.';
    } catch (err) {
      $('server-status').textContent = err.message;
    }
  });
  let serverTimer = 0;
  const saveServer = () => {
    if (!token) return;
    clearTimeout(serverTimer);
    serverTimer = setTimeout(() => {
      saveMe({ custom_host: val($('server-host')), custom_port: val($('server-port')) }).catch(() => {});
    }, 400);
  };
  $('server-host').addEventListener('input', saveServer);
  $('server-port').addEventListener('input', saveServer);
  const boost = $('mic-boost');
  const silence = $('silence');
  boost.value = localStorage.getItem('recortana_mic_boost') || '1.5';
  silence.value = localStorage.getItem('recortana_silence') || '1';
  const paintSliders = () => {
    $('mic-boost-value').textContent = Number(boost.value).toFixed(1) + '×';
    $('silence-value').textContent = Number(silence.value).toFixed(1) + 's';
  };
  paintSliders();
  boost.addEventListener('input', () => {
    localStorage.setItem('recortana_mic_boost', boost.value);
    paintSliders();
  });
  silence.addEventListener('input', () => {
    localStorage.setItem('recortana_silence', silence.value);
    paintSliders();
  });
  $('ha-connect').addEventListener('click', async () => {
    const base = val($('ha-url')).replace(/\/$/, '');
    if (!base) { $('ha-message').textContent = 'Enter your Home Assistant URL first.'; return; }
    try {
      const result = await api('/ha/start', { method: 'POST', body: JSON.stringify({ base_url: base }) });
      if (result.authorize_url) location.href = result.authorize_url;
      else $('ha-message').textContent = result.message || 'Could not start Home Assistant login.';
    } catch (err) { $('ha-message').textContent = err.message; }
  });
  $('ha-disconnect').addEventListener('click', async () => {
    await api('/ha/link', { method: 'DELETE' });
    $('profile-ha').textContent = 'Not connected';
    $('ha-message').textContent = 'Disconnected.';
  });
}

async function boot() {
  frame = $('appFrame');
  settingsPage = $('settingsPage');
  detailPage = $('detailPage');
  anim = new CortanaAnimation($('circle'));
  applyAccent(localStorage.getItem(ACCENT_KEY) || '#0078D7');
  const app = new DiscoApp({ theme: 'dark', accent: localStorage.getItem(ACCENT_KEY) || '#0078D7', font: 'Segoe UI', splash: 'none', scale: 1 });
  app.scale = 1;
  app.launch(frame);
  wire();
  new ResizeObserver(fitSettings).observe($('stage'));
  fitSettings();
  anim.play(AnimState.ENTRANCE).catch((err) => { showIdle(err.message || 'Animation failed to load.'); });

  if (!token) {
    showIdle('Sign in under settings to keep this conversation.');
    paintProfile();
    return;
  }
  try {
    me = await api('/me');
  } catch (err) {
    if (err.status === 401) {
      token = '';
      localStorage.removeItem(TOKEN_KEY);
    }
    showIdle(err.message || 'Sign in under settings.');
    paintProfile();
    return;
  }
  paintProfile();
  refreshHa();
  showIdle(greeting());
  refreshFeed();
  const params = new URLSearchParams(location.search);
  if (params.get('ha_link') === '1') $('ha-message').textContent = 'Home Assistant connected.';
  if (params.get('ha_error')) $('ha-message').textContent = 'Home Assistant error: ' + params.get('ha_error');
  if (params.get('ha_link') || params.get('ha_error')) history.replaceState({}, '', '/');
}

boot().catch((err) => {
  const line = $('results-display');
  if (line) line.textContent = err.message || 'Could not start.';
});
